import { useCallback } from 'react';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { COMMERCE_PAYMENT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { getInvoiceProperty } from '../utils/property';
import { HS_ENABLE_AUTOMATED_TAXES, HS_PAYMENT_METHOD_TYPE, HS_TAXES_TOTAL } from '../constants/properties';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
const useRecordPayment = (invoice, setIsRecordPaymentPanelOpen, refetchInvoice, pollForInvoiceUpdates, updatePreview, maybeUpdatePaymentIndexTable, updatePaymentsSidebar, onFailure, onFailureOutdated, onFailureGeneric, trackUsage) => {
  const getSalesTaxType = useCallback(() => {
    const automatedSalesTaxEnabled = getInvoiceProperty(invoice, HS_ENABLE_AUTOMATED_TAXES) === 'true';
    const salesTaxTotal = getInvoiceProperty(invoice, HS_TAXES_TOTAL);
    const hasSalesTax = !!salesTaxTotal && !isNaN(+salesTaxTotal) && +salesTaxTotal > 0;
    return automatedSalesTaxEnabled ? 'AUTO' : hasSalesTax ? 'MANUAL' : 'NONE';
  }, [invoice]);
  const onFailureRecordPayment = useCallback(error => {
    var _error$responseJSON;
    const OUT_OF_DATE_ERROR = 'ExternalPaymentValidation.INVOICE_OUT_OF_DATE';
    if ((error === null || error === void 0 || (_error$responseJSON = error.responseJSON) === null || _error$responseJSON === void 0 ? void 0 : _error$responseJSON.subCategory) === OUT_OF_DATE_ERROR) {
      onFailureOutdated();
    } else {
      onFailure();
    }
    refetchInvoice(invoice.objectId).catch(() => onFailureGeneric());
    setIsRecordPaymentPanelOpen(false);
  }, [invoice.objectId, onFailure, onFailureGeneric, onFailureOutdated, refetchInvoice, setIsRecordPaymentPanelOpen]);
  const handleRecordPaymentOnAdd = useCallback(async numberAdded => {
    FloatingAlertStore.addAlert({
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoices.alerts.recordPayment.successAdd",
        options: {
          count: numberAdded
        }
      }),
      type: 'success'
    });
    trackUsage({
      action: 'Add existing payment',
      salesTax: getSalesTaxType()
    });
    const updatedInvoice = await pollForInvoiceUpdates();
    if (updatedInvoice) {
      refetchInvoice(invoice.objectId).catch(() => onFailureGeneric());
    }
    setIsRecordPaymentPanelOpen(false);
  }, [getSalesTaxType, invoice.objectId, onFailureGeneric, pollForInvoiceUpdates, refetchInvoice, trackUsage, setIsRecordPaymentPanelOpen]);
  const handleRecordPaymentOnCreate = useCallback(async commercePaymentObject => {
    var _commercePaymentObjec;
    FloatingAlertStore.addAlert({
      message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "invoices.alerts.recordPayment.successNew_jsx",
        elements: {
          UILink: Link
        },
        options: {
          onClick: () => updatePreview(COMMERCE_PAYMENT_TYPE_ID, String(commercePaymentObject.objectId))
        }
      }),
      type: 'success'
    });
    trackUsage({
      action: 'Add new payment',
      salesTax: getSalesTaxType(),
      paymentMethod: (_commercePaymentObjec = commercePaymentObject.properties.find(property => property.name === HS_PAYMENT_METHOD_TYPE)) === null || _commercePaymentObjec === void 0 ? void 0 : _commercePaymentObjec.value
    });
    const updatedInvoice = await pollForInvoiceUpdates();
    if (updatedInvoice) {
      refetchInvoice(invoice.objectId).catch(() => onFailureGeneric());
      maybeUpdatePaymentIndexTable(commercePaymentObject.objectId);
      updatePaymentsSidebar(commercePaymentObject.objectId);
    }
    setIsRecordPaymentPanelOpen(false);
  }, [getSalesTaxType, invoice.objectId, onFailureGeneric, pollForInvoiceUpdates, refetchInvoice, trackUsage, updatePreview, maybeUpdatePaymentIndexTable, setIsRecordPaymentPanelOpen, updatePaymentsSidebar]);
  return {
    onFailureRecordPayment,
    handleRecordPaymentOnAdd,
    handleRecordPaymentOnCreate
  };
};
export default useRecordPayment;